import * as React from "react";
import styles from "../../../assets/scss/components/leadPages/leadForm/leadForm.module.scss";
import EdButton from "../../generic/edButton";
import { emailRegex, integerExp, phoneNo, alphabetExp } from "../../utils/regex";
import { IListObj } from "../../types/genericTypes";
import { apiService } from "../../service/apiService";
import urlConfig from "../../../config/domain";
import { isLoggedIn } from "../../service/login/loginService";
import bStrg from "./../../service/storageHelper";
import gtmService from "../../service/gtmService";
import { getLeadMasterDetails } from "../../service/postLogin/leadAPIService";

type TFieldNames = "name" | "email" | "phone" | "city" | "type";

type TFieldValues = {
	[x in TFieldNames]: string;
};

type TFieldValidity = {
	[x in TFieldNames]: Partial<ValidityState> & { [x: string]: boolean | undefined };
};

type TShowError = {
	[x in TFieldNames]: boolean;
};

export type TSubmitState = "initiated" | "submitted" | "success" | "error";

export interface LeadFormProps {
	color?: "grey" | "white"; // colour of fields
	className?: string; // className on form
	submitStateChange: (state: TSubmitState) => void;
	prefill?: boolean;
	gtmAction: "Request_a_call_Banner" | "Request_a_call_Floating_Banner" | "Request_a_call_Bottom_Banner";
}

let resetFormSubscription: NodeJS.Timeout | undefined = undefined;

const LeadForm: React.FC<LeadFormProps> = (props) => {
	let color = props.color || "grey";
	const radioList: IListObj[] = [
		{ display: "Life", value: "life" },
		{ display: "Health", value: "health" },
		{ display: "Motor", value: "motor" }
	];

	const [values, setValues] = React.useState<TFieldValues>({ name: "", email: "", phone: "", city: "", type: "life" });
	const [error, setError] = React.useState<TFieldValidity>({
		name: {},
		email: {},
		phone: {},
		city: {},
		type: { valid: true }
	});
	const [showError, setShowError] = React.useState<TShowError>({
		name: false,
		email: false,
		phone: false,
		city: false,
		type: false
	});
	const [submitState, setSubmitState] = React.useState<TSubmitState>("initiated");

	React.useEffect(() => {
		if (submitState !== "initiated") props.submitStateChange(submitState);
	}, [submitState]);

	// if the user is logged in then fill details automatically
	React.useEffect(() => {
		setInitialValues();

		return () => {
			resetFormSubscription && clearTimeout(resetFormSubscription);
		};
	}, []);

	function setInitialValues() {
		const isLgdIn = isLoggedIn();
		if (isLgdIn && props.prefill && bStrg("PersonalDetails")) {
			setValues({
				name: bStrg("PersonalDetails").nameField,
				email: bStrg("PersonalDetails").emailAddressField,
				phone: bStrg("PersonalDetails").mobileNumberField,
				city: "",
				type: "life"
			});
			setError({ name: { valid: true }, email: { valid: true }, phone: { valid: true }, city: {}, type: { valid: true } });
		} else {
			setValues({ name: "", email: "", phone: "", city: "", type: "life" });
			setError({ name: {}, email: {}, phone: {}, city: {}, type: { valid: true } });
		}
		setShowError({ name: false, email: false, phone: false, city: false, type: false });
	}

	function fieldChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
		let { name, value, validity } = e.target;

		// stop showing error if it is being shown
		if (showError[name as TFieldNames]) {
			setShowError({ ...showError, [name]: false });
		}

		if (name === "email" && !emailRegex.test(value)) {
			e.target.setCustomValidity("pattern");
			(validity as any).pattern = true;
		} else if ((name === "name" || name === "city") && !alphabetExp.test(value)) {
			e.target.setCustomValidity("pattern");
			(validity as any).pattern = true;
		} else {
			e.target.setCustomValidity("");
			(validity as any).pattern = false;
		}

		setValues({ ...values, [name]: value });
		setError({ ...error, [name]: validity });
	}

	function phoneChange(e: React.ChangeEvent<HTMLInputElement>) {
		let { name, value, validity } = e.target;

		// stop showing error if it is being shown
		if (showError[name as TFieldNames]) {
			setShowError({ ...showError, [name]: false });
		}

		if (!integerExp.test(value)) {
			// only allow integers in mobile
			value = values.phone;
		}

		setValues({ ...values, [name]: value });

		if (value && !phoneNo.test(value)) {
			e.target.setCustomValidity("phoneNo");
			(validity as any).phoneNo = true;
		} else if (value && +value < 1000000000) {
			e.target.setCustomValidity("minValue");
			(validity as any).minValue = true;
		} else {
			e.target.setCustomValidity("");
			(validity as any).phoneNo = false;
			(validity as any).minValue = false;
		}

		setError({ ...error, [name]: validity });
	}

	async function submitLead(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const temp: any = {};
		setSubmitState("submitted");
		
		if (values.type === "life") {
			let resp = await getLeadMasterDetails("15");
			if (resp) {
			  //temp.ProductNames = "Life Insurance";
			  temp.ProductId = resp && resp.length > 0 ? resp[0].product_code : "";
			  temp.CategoryId = resp && resp.length > 0 ? resp[0].category_code : "";
			  temp.SubCategoryId = resp && resp.length > 0 ? resp[0].subcategory_code : "";
			  temp.SourceId = resp && resp.length > 0 ? resp[0].source_code : "";
			  temp.SubSourceId = resp && resp.length > 0 ? resp[0].subsource_code : ""
			  gtmService({ event: "event footer", category: "All Insurance | Form_Submit", action: props.gtmAction, label: "Life" });
			  const body = {
				Name: values.name,
				Email: values.email,
				Phone: values.phone,
				City: values.city,
				...temp,
				OfferId : 0,
                EntityType : "" 
			};
	
			apiService({ url: urlConfig.oyo + "saveleadinfo_V1", method: "POST", body }, submitRes);
	
			// reset form after 3 sec
			resetFormSubscription = setTimeout(() => {
				if (resetFormSubscription) {
					setSubmitState("initiated");
					setInitialValues();
				}
			}, 3000);
			}
		} else if (values.type === "health") {
			let resp = await getLeadMasterDetails("16");
			if (resp) {
			  //temp.ProductNames = "General Insurance";
			  temp.ProductId = resp && resp.length > 0 ? resp[0].product_code : "";
			  temp.CategoryId = resp && resp.length > 0 ? resp[0].category_code : "";
			  temp.SubCategoryId = resp && resp.length > 0 ? resp[0].subcategory_code : "";
			  temp.SourceId = resp && resp.length > 0 ? resp[0].source_code : "";
			  temp.SubSourceId = resp && resp.length > 0 ? resp[0].subsource_code : ""
			  gtmService({ event: "event footer", category: "All Insurance | Form_Submit", action: props.gtmAction, label: "Health" });

			  const body = {
				Name: values.name,
				Email: values.email,
				Phone: values.phone,
				City: values.city,
				...temp,
				OfferId : 0,
                EntityType : "" 
			};
	
			apiService({ url: urlConfig.oyo + "saveleadinfo_V1", method: "POST", body }, submitRes);
	
			// reset form after 3 sec
			resetFormSubscription = setTimeout(() => {
				if (resetFormSubscription) {
					setSubmitState("initiated");
					setInitialValues();
				}
			}, 3000);
			}
		} else if (values.type === "motor") {
			let resp = await getLeadMasterDetails("17");
			if (resp) {
			  //temp.ProductNames = "General Insurance";
			  temp.ProductId = resp && resp.length > 0 ? resp[0].product_code : "";
			  temp.CategoryId = resp && resp.length > 0 ? resp[0].category_code : "";
			  temp.SubCategoryId = resp && resp.length > 0 ? resp[0].subcategory_code : "";
			  temp.SourceId = resp && resp.length > 0 ? resp[0].source_code : "";
			  temp.SubSourceId = resp && resp.length > 0 ? resp[0].subsource_code : ""
			  gtmService({ event: "event footer", category: "All Insurance | Form_Submit", action: props.gtmAction, label: "Motor" });
			  const body = {
				Name: values.name,
				Email: values.email,
				Phone: values.phone,
				City: values.city,
				...temp,
				OfferId : 0,
                EntityType : "" 
			};
	
			apiService({ url: urlConfig.oyo + "saveleadinfo_V1", method: "POST", body }, submitRes);
	
			// reset form after 3 sec
			resetFormSubscription = setTimeout(() => {
				if (resetFormSubscription) {
					setSubmitState("initiated");
					setInitialValues();
				}
			}, 3000);
			}
		}
	}

	function submitRes(res: string) {
		if (res && !res.toLowerCase().includes("failure")) {
			setSubmitState("success");
		} else {
			setSubmitState("error");
		}
	}

	function temp(obj: IListObj) {
		setValues({ ...values, type: obj.value as string });
	}

	return (
		<form className={[styles.lead_form, styles[color], props.className].join(" ")} onSubmit={submitLead}>
			<div className={styles.inp_grp}>
				<label className={styles.label} htmlFor="name">
					Name
				</label>
				<input
					value={values.name}
					className={styles.field + (showError.name && !error.name.valid ? " " + styles.invalid : "")}
					placeholder="Eg: Dinesh Patel"
					name="name"
					id="name"
					autoComplete="off"
					required
					maxLength={100}
					onChange={fieldChange}
					onBlur={() => setShowError({ ...showError, name: true })}
				/>
				{showError.name && !error.name.valid && (
					<span className={styles.errMsg}>
						{error.name.valueMissing || error.name.valueMissing === undefined ? "This field is mandatory" : "Invalid input"}
					</span>
				)}
			</div>

			<div className={styles.inp_grp}>
				<label className={styles.label} htmlFor="email">
					Email
				</label>
				<input
					value={values.email}
					className={styles.field + (showError.email && !error.email.valid ? " " + styles.invalid : "")}
					placeholder="Eg: dinesh@gmail.com"
					name="email"
					id="email"
					autoComplete="off"
					required
					maxLength={50}
					onChange={fieldChange}
					onBlur={() => setShowError({ ...showError, email: true })}
				/>
				{showError.email && !error.email.valid && (
					<span className={styles.errMsg}>
						{error.email.valueMissing || error.email.valueMissing === undefined ? "This field is mandatory" : "Invalid input"}
					</span>
				)}
			</div>

			<div className={styles.inp_grp}>
				<label className={styles.label} htmlFor="phone">
					Mobile Number
				</label>
				<div className={styles.field_group}>
					<span className={styles.country_cd}>+91</span>
					<div>
						<input
							type="tel"
							value={values.phone}
							className={styles.field + (showError.phone && !error.phone.valid ? " " + styles.invalid : "")}
							placeholder="Eg: 9876678945"
							name="phone"
							id="phone"
							autoComplete="off"
							required
							maxLength={10}
							onChange={phoneChange}
							onBlur={() => setShowError({ ...showError, phone: true })}
						/>
						{showError.phone && !error.phone.valid && (
							<span className={styles.errMsg}>
								{error.phone.valueMissing || error.phone.valueMissing === undefined ? "This field is mandatory" : "Invalid input"}
							</span>
						)}
					</div>
				</div>
			</div>

			<div className={styles.inp_grp}>
				<label className={styles.label} htmlFor="city">
					City
				</label>
				<input
					value={values.city}
					className={styles.field + (showError.city && !error.city.valid ? " " + styles.invalid : "")}
					placeholder="Eg: Mumbai"
					name="city"
					id="city"
					autoComplete="off"
					required
					onChange={fieldChange}
					onBlur={() => setShowError({ ...showError, city: true })}
				/>
				{showError.city && !error.city.valid && (
					<span className={styles.errMsg}>
						{error.city.valueMissing || error.city.valueMissing === undefined ? "This field is mandatory" : "Invalid input"}
					</span>
				)}
			</div>

			<label className={styles.label}>Type of Insurance you’re interested in:</label>
			<div>
				{radioList.map((obj) => (
					<LeadFormRadio
						key={obj.value}
						display={obj.display}
						value={obj.value as string}
						name="type"
						id={obj.value as string}
						checked={values.type === obj.value}
						onChange={() => temp(obj)}
					/>
				))}
			</div>

			<EdButton
				type="submit"
				btnTyp={color === "grey" ? "primary" : "secondary"}
				value="Request a Call"
				className={styles.submit}
				disabled={!!Object.keys(error).find((err) => !error[err as TFieldNames].valid) || submitState !== "initiated"}
			/>
		</form>
	);
};

interface LeadFormRadioProps {
	value: string;
	display: string;
	name: string;
	id: string;
	checked: boolean;
	onChange: () => void;
}

const LeadFormRadio: React.FC<LeadFormRadioProps> = ({ display, ...props }) => {
	return (
		<label className={styles.radio}>
			<input type="radio" {...props} />
			<span className={styles.checkmark}></span>
			<label className={styles.display} htmlFor={props.id}>
				{" "}
				{display}
			</label>
		</label>
	);
};

export default LeadForm;
